// API caching utility to prevent multiple calls to external APIs
import axios from 'axios';

class APICache {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
  }

  // Cache IP address to prevent multiple calls to api.ipify.org
  async getIPAddress() {
    const cacheKey = 'ip_address';

    // Return cached result if available
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Return pending request if one exists
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    // Create new request
    const request = this.fetchIPAddress();
    this.pendingRequests.set(cacheKey, request);

    try {
      const result = await request;
      this.cache.set(cacheKey, result);
      this.pendingRequests.delete(cacheKey);
      return result;
    } catch (error) {
      this.pendingRequests.delete(cacheKey);
      throw error;
    }
  }

  async fetchIPAddress() {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  }

  // Cache states data to prevent multiple calls to countriesnow.space
  async getStatesForCountry(countryName) {
    const cacheKey = `states_${countryName}`;

    // Return cached result if available
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Return pending request if one exists
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    // Create new request
    const request = this.fetchStatesForCountry(countryName);
    this.pendingRequests.set(cacheKey, request);

    try {
      const result = await request;
      this.cache.set(cacheKey, result);
      this.pendingRequests.delete(cacheKey);
      return result;
    } catch (error) {
      this.pendingRequests.delete(cacheKey);
      throw error;
    }
  }

  async fetchStatesForCountry(countryName) {
    const response = await fetch(
      `https://countriesnow.space/api/v0.1/countries/states/q?country=${encodeURIComponent(countryName)}`,
      {
        headers: {
          'Access-Control-Allow-Origin': '*'
        }
      });
    const data = await response.json();

    if (data && data.data && data.data.states && data.data.states.length > 0) {
      return data.data.states.map(s => ({
        code: (s.state_code || s.name || s).toUpperCase().slice(0, 10),
        name: s.name || s.state || s
      }));
    }
    return [];
  }

  // Clear cache (useful for testing or when you want to force refresh)
  clearCache() {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  // Cache account information to prevent multiple calls to /normal-user/account-information
  async getAccountInformation() {
    const cacheKey = 'account_information';

    // Return cached result if available
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Return pending request if one exists
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    // Create new request
    const request = this.fetchAccountInformation();
    this.pendingRequests.set(cacheKey, request);

    try {
      const result = await request;
      this.cache.set(cacheKey, result);
      this.pendingRequests.delete(cacheKey);
      return result;
    } catch (error) {
      this.pendingRequests.delete(cacheKey);
      throw error;
    }
  }

  async fetchAccountInformation() {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API}/normal-user/account-information`,
      {
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  }

  // Clear specific cache entry
  clearCacheEntry(key) {
    this.cache.delete(key);
    this.pendingRequests.delete(key);
  }

  // Clear account information cache (useful when user data changes)
  clearAccountInformationCache() {
    this.clearCacheEntry('account_information');
  }

  // Helper method to get paymentTokenId from cached account information
  async getPaymentTokenId() {
    try {
      const accountData = await this.getAccountInformation();
      return accountData?.data?.paymentTokenId || null;
    } catch (error) {
      return null;
    }
  }
}

// Create singleton instance
const apiCache = new APICache();

export default apiCache;
